import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Container,
  Heading,
  HStack,
  IconButton,
  Image,
  Link,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { FavouritesMenuSection } from 'apps/TenantApp/components/DesktopMenu/FavouritesMenuSection';
import { MobileMenuEntry } from 'apps/TenantApp/components/MobileMenuEntry';
import { useSearchModal } from 'apps/TenantApp/components/SearchModal/useSearchModal';
import BasicBar from 'components/bars/BasicBar';
import {
  AddItemIcon,
  CalendarIcon,
  CircleIcon,
  TodoIcon,
  VaultIcon,
} from 'components/icons';
import { RelayPullToRefresh } from 'components/PullToRefresh';
import SearchField from 'components/SearchField';
import { ScreenWithBar } from 'layouts/ScreenWithBar';
import { orderBy } from 'natural-orderby';
import React, { ComponentProps, PropsWithChildren } from 'react';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import {
  CALENDAR_PATH,
  ORG_SETTINGS_PARAM_NAME,
  TENNANT_SETTINGS_SPACES_PATH_PREFIX,
  TODO_PATH,
  VAULTS_MY_VAULT_PATH,
} from 'utils/routes';
import { useFeatureFlag } from 'utils/sessionProvider';
import { SpaceCard } from '../OrganisationSettings/screens/SpacesSettings/components/SpaceCard';
import { SpacesList } from './components/SpacesList';
import { Start_page_Query } from './__generated__/Start_page_Query.graphql';

export const spacesPageQuery = graphql`
  query Start_page_Query {
    sessionInfo {
      tenant {
        id
        canCreateSpace: canUpdate {
          value
        }
        icon {
          url
        }
      }
      member {
        spaceMemberships(first: 500)
          @connection(key: "Session_spaceMemberships") {
          edges {
            node {
              space {
                id
                name
                ...SpaceCard_space
              }
            }
          }
        }
      }
      ...FavouritesMenuSection
    }
  }
`;

const StartScreen = () => {
  const { openSearch } = useSearchModal();

  const { useLazyLoadQueryWithRef } = useSafeRelay();
  const [{ sessionInfo }, queryRef] = useLazyLoadQueryWithRef<Start_page_Query>(
      spacesPageQuery,
      {},
    ),
    canCreateSpace = sessionInfo?.tenant?.canCreateSpace?.value;

  const nodes =
    sessionInfo?.member?.spaceMemberships?.edges?.map(
      (edge) => edge?.node?.space,
    ) || [];
  const spaces = orderBy(nodes.filter(notEmpty), [(s) => s.name], 'asc');
  const hasSpaces = spaces.length > 0;

  const featureMobileBrandingEnabled = useFeatureFlag(
    'FEATURE_MOBILE_BRANDING',
  );

  return (
    <ScreenWithBar
      bar={
        <BasicBar>
          <HStack>
            {featureMobileBrandingEnabled && sessionInfo.tenant?.icon?.url ? (
              <Image
                src={sessionInfo.tenant?.icon?.url}
                borderWidth="1px"
                borderRadius="xs"
                borderColor="white"
                borderStyle="solid"
                alt=""
                boxSize="25px"
                objectFit="cover"
              />
            ) : (
              <CircleIcon />
            )}

            <Text>{t`Start`}</Text>
          </HStack>
        </BasicBar>
      }
    >
      <RelayPullToRefresh queryRef={queryRef}>
        <Container pt={5}>
          <Stack spacing={6}>
            <SearchField
              size="md"
              placeholder={t`Search`}
              onClick={() => openSearch()}
            />
            <Stack>
              <MenuSubtitle>{t`Personal`}</MenuSubtitle>
              <SimpleGrid
                columns={2}
                columnGap={4}
                rowGap={4}
                autoRows="1fr"
                minChildWidth="110px"
              >
                <MobileMenuEntry
                  title={t`Calendar`}
                  icon={<CalendarIcon />}
                  to={CALENDAR_PATH}
                />
                <MobileMenuEntry
                  title={t`Todo`}
                  icon={<TodoIcon />}
                  to={TODO_PATH}
                />
                <MobileMenuEntry
                  title={t`My vaults`}
                  icon={<VaultIcon />}
                  to={VAULTS_MY_VAULT_PATH}
                />
              </SimpleGrid>
            </Stack>
            <Stack>
              <FavouritesMenuSection
                isMobile
                favouritesFragment={sessionInfo}
              />
            </Stack>
            <Stack>
              <HStack>
                <MenuSubtitle
                  rightButton={
                    <SubtitleIconButton
                      active={canCreateSpace}
                      as={Link}
                      to={`?${ORG_SETTINGS_PARAM_NAME}=${TENNANT_SETTINGS_SPACES_PATH_PREFIX}`}
                      icon={<AddItemIcon />}
                      aria-label={t`Add a space`}
                    />
                  }
                >
                  {t`Spaces`}
                </MenuSubtitle>
              </HStack>
              {hasSpaces ? (
                <SpacesList>
                  {spaces.map((space) => {
                    return (
                      <SpaceCard
                        key={space.id}
                        space={space}
                        useAvailableHeight
                      />
                    );
                  })}
                </SpacesList>
              ) : (
                <NoSpacesExplanation />
              )}
            </Stack>
          </Stack>
        </Container>
      </RelayPullToRefresh>
    </ScreenWithBar>
  );
};

const NoSpacesExplanation = () => {
  return (
    <Alert status="info">
      <AlertIcon />
      <Box flex="1">
        <AlertTitle>{t`You must be new here!`}</AlertTitle>
        <AlertDescription display="block">
          {t`Either no spaces have been created or you aren't invited to one yet.`}
        </AlertDescription>
      </Box>
    </Alert>
  );
};

const MenuSubtitle = ({
  children,
  rightButton,
}: PropsWithChildren<{ rightButton?: React.ReactElement | false }>) => {
  return (
    <HStack w="100%" h="1rem" overflowY="visible">
      <Heading fontSize="xs">{children}</Heading>
      <Spacer />
      {rightButton}
    </HStack>
  );
};

export const SubtitleIconButton = ({
  active,
  ...props
}: ComponentProps<typeof IconButton> & { active?: boolean }) =>
  active === false ? null : (
    <IconButton
      variant="ghost"
      _hover={{ bg: 'transparent' }}
      size="xs"
      color="inherit"
      {...props}
    />
  );

export default StartScreen;
